import type { Vaccination } from '../types/medical.types';

/**
 * Servicio de l�gica de negocio para vacunas
 * Funciones puras sin dependencias de React
 */
export class VaccinationService {
  // Constantes de negocio
  private static readonly DAYS_BEFORE_DUE_WARNING = 30; // d�as

  /**
   * Detecta vacunas pr�ximas a vencer (dentro de los pr�ximos 30 d�as)
   */
  static getUpcomingVaccinations(vaccinations: Vaccination[]): Vaccination[] {
    const now = new Date();
    const warningDate = new Date();
    warningDate.setDate(now.getDate() + this.DAYS_BEFORE_DUE_WARNING);

    return vaccinations.filter((vaccine) => {
      if (!vaccine.nextDueDate) return false;

      const dueDate = new Date(vaccine.nextDueDate);
      return dueDate >= now && dueDate <= warningDate;
    });
  }

  /**
   * Detecta vacunas ya vencidas
   */
  static getOverdueVaccinations(vaccinations: Vaccination[]): Vaccination[] {
    const now = new Date();

    return vaccinations.filter((vaccine) => {
      if (!vaccine.nextDueDate) return false;

      const dueDate = new Date(vaccine.nextDueDate);
      return dueDate < now;
    });
  }

  /**
   * Calcula d�as restantes hasta el vencimiento de una vacuna
   * Retorna n�mero positivo si a�n no vence, negativo si ya venci�
   */
  static getDaysUntilDue(vaccination: Vaccination): number | null {
    if (!vaccination.nextDueDate) return null;

    const now = new Date();
    const dueDate = new Date(vaccination.nextDueDate);

    const diffTime = dueDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays;
  }

  /**
   * Determina el estado de una vacuna basado en su fecha de vencimiento
   * - 'current': Vigente (vence en m�s de 30 d�as)
   * - 'upcoming': Pr�xima a vencer (vence en 30 d�as o menos)
   * - 'overdue': Vencida
   * - 'no-due-date': Sin fecha de vencimiento
   */
  static getVaccinationStatus(
    vaccination: Vaccination
  ): 'current' | 'upcoming' | 'overdue' | 'no-due-date' {
    if (!vaccination.nextDueDate) return 'no-due-date';

    const daysUntilDue = this.getDaysUntilDue(vaccination);

    if (daysUntilDue === null) return 'no-due-date';
    if (daysUntilDue < 0) return 'overdue';
    if (daysUntilDue <= this.DAYS_BEFORE_DUE_WARNING) return 'upcoming';
    return 'current';
  }

  /**
   * Ordena vacunas por fecha de vencimiento (pr�ximas primero)
   */
  static sortByDueDate(vaccinations: Vaccination[]): Vaccination[] {
    return [...vaccinations].sort((a, b) => {
      // Vacunas sin fecha de vencimiento van al final
      if (!a.nextDueDate && !b.nextDueDate) return 0;
      if (!a.nextDueDate) return 1;
      if (!b.nextDueDate) return -1;

      const dateA = new Date(a.nextDueDate);
      const dateB = new Date(b.nextDueDate);

      return dateA.getTime() - dateB.getTime();
    });
  }

  /**
   * Filtra vacunas activas (que tienen fecha de pr�xima aplicaci�n)
   */
  static getActiveVaccinations(vaccinations: Vaccination[]): Vaccination[] {
    return vaccinations.filter((vaccine) => vaccine.nextDueDate !== undefined);
  }

  /**
   * Valida fechas de vacuna
   */
  static validateVaccinationDates(administeredDate: string, nextDueDate?: string): {
    valid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    // Fecha de administraci�n no puede ser futura
    const adminDate = new Date(administeredDate);
    const now = new Date();

    if (adminDate > now) {
      errors.push('La fecha de administraci�n no puede ser futura');
    }

    // Si hay nextDueDate, debe ser posterior a administeredDate
    if (nextDueDate) {
      const dueDate = new Date(nextDueDate);

      if (dueDate <= adminDate) {
        errors.push('La fecha de vencimiento debe ser posterior a la fecha de administraci�n');
      }
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }
}
